
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Users, DollarSign, Phone, Mail, Clock, Accessibility, Lock } from 'lucide-react';
import { BedListing } from '@/types/listings';
import { AssistedLivingScorecard } from '@/types/scorecard';
import { ScorecardService } from '@/services/ScorecardService';
import ScorecardDisplay from '@/components/scorecard/ScorecardDisplay';
import ReferralEmailModal from '@/components/referrals/ReferralEmailModal';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';

interface ListingCardProps {
  listing: BedListing;
  onViewDetails?: (listing: BedListing) => void;
  onSubmitReferral?: (listing: BedListing) => void;
  showReferralButton?: boolean;
}

const ListingCard: React.FC<ListingCardProps> = ({
  listing,
  onViewDetails,
  onSubmitReferral,
  showReferralButton = true,
}) => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [scorecard, setScorecard] = useState<AssistedLivingScorecard | null>(null);
  const [loadingScorecard, setLoadingScorecard] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const availabilityColor = listing.beds_available > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  const isAuthenticated = !!user;
  const isProvider = profile?.role === 'provider';
  const isOwnListing = user && listing.created_by === user.id;

  useEffect(() => {
    const fetchScorecard = async () => {
      setLoadingScorecard(true);
      try {
        const scorecardData = await ScorecardService.lookupScorecard({
          facilityName: listing.facility_name,
          county: listing.county
        });
        
        if (!scorecardData) {
          // Try to scrape data if not found
          const scrapedData = await ScorecardService.scrapeScorecard(listing.facility_name);
          setScorecard(scrapedData);
        } else {
          setScorecard(scorecardData);
        }
      } catch (error) {
        console.error('Error fetching scorecard:', error);
      } finally {
        setLoadingScorecard(false);
      }
    };

    fetchScorecard();
  }, [listing.facility_name, listing.county]);

  const handleContactAction = () => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  };

  return (
    <Card className="w-full hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg">{listing.facility_name}</CardTitle>
          <Badge className={availabilityColor}>
            {listing.beds_available > 0 ? `${listing.beds_available} Available` : 'No Beds Available'}
          </Badge>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          <Badge variant="outline">{listing.care_level}</Badge>
          <Badge variant="outline">{listing.county} County</Badge>
          <Badge variant="outline">{listing.age_group}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{listing.location}</span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Updated {formatDistanceToNow(new Date(listing.last_updated))} ago</span>
            </div>
            <div className="flex items-center space-x-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">${listing.rent_amount}/month</span>
            </div>
          </div>
        </div>

        {listing.description && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Description:</div>
            <p className="text-sm text-muted-foreground">{listing.description}</p>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">{listing.beds_available} of {listing.licensed_capacity} licensed beds</span>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Waiver Types:</div>
          <div className="flex flex-wrap gap-1">
            {listing.waiver_types.map(waiver => (
              <Badge key={waiver} variant="secondary" className="text-xs">
                {waiver}
              </Badge>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Gender Accepted:</div>
          <div className="flex flex-wrap gap-1">
            {listing.gender_accepted.map(gender => (
              <Badge key={gender} variant="secondary" className="text-xs">
                {gender}
              </Badge>
            ))}
          </div>
        </div>


        {listing.accessibility && listing.accessibility.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Accessibility Features:</div>
            <div className="flex items-center space-x-2">
              <Accessibility className="h-4 w-4 text-muted-foreground" />
              <div className="flex flex-wrap gap-1">
                {listing.accessibility.map(feature => (
                  <Badge key={feature} variant="secondary" className="text-xs">
                    {feature}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Minnesota Quality Scorecard */}
        {scorecard && (
          <div className="pt-4 border-t">
            <ScorecardDisplay scorecard={scorecard} compact />
          </div>
        )}
        
        {loadingScorecard && (
          <div className="pt-4 border-t">
            <div className="text-sm text-muted-foreground">Loading quality ratings...</div>
          </div>
        )}

        {/* Contact Information - Hidden from unauthenticated users and other providers */}
        <div className="space-y-2">
          {isAuthenticated && (!isProvider || isOwnListing) ? (
            <>
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{listing.contact_person} - {listing.contact_phone}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{listing.contact_email}</span>
              </div>
            </>
          ) : !isAuthenticated ? (
            <div className="bg-muted/50 border border-dashed border-muted-foreground/30 rounded-lg p-4">
              <div className="flex items-center justify-center space-x-2 text-muted-foreground">
                <Lock className="h-4 w-4" />
                <span className="text-sm font-medium">Contact information available after login</span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full mt-2"
                onClick={handleContactAction}
              >
                Sign in to view contact details
              </Button>
            </div>
          ) : (
            <div className="bg-muted/50 border border-dashed border-muted-foreground/30 rounded-lg p-4">
              <div className="flex items-center justify-center space-x-2 text-muted-foreground">
                <Lock className="h-4 w-4" />
                <span className="text-sm font-medium">Contact information not available to other providers</span>
              </div>
            </div>
          )}
        </div>


        <div className="flex space-x-2 pt-4">
          <Button 
            variant="outline" 
            onClick={() => onViewDetails?.(listing)}
          >
            View Details
          </Button>
          {showReferralButton && listing.beds_available > 0 && (
            <Button 
              onClick={() => {
                if (!isAuthenticated) {
                  navigate('/login');
                  return;
                }
                setShowReferralModal(true);
              }}
            >
              {isAuthenticated ? 'Submit Referral' : 'Sign in to Submit Referral'}
            </Button>
          )}
        </div>
      </CardContent>

      <ReferralEmailModal
        isOpen={showReferralModal}
        onClose={() => setShowReferralModal(false)}
        provider={{
          id: listing.id,
          facility_name: listing.facility_name,
          contact_email: listing.contact_email,
          contact_person: listing.contact_person,
        }}
      />
    </Card>
  );
};

export default ListingCard;
